// Application Configuration - Centralized settings for the entire application
// This file contains global configuration values that affect the behavior and appearance of the app

export const appConfig = {
  // ============================================================================
  // DATE & TIME FORMATTING
  // ============================================================================
  
  /**
   * Date format used throughout the application
   * Used in tables, forms, displays, etc.
   */
  dateFormat: 'dd/MM/yyyy',
  
  /**
   * Date-time format for detailed timestamps
   */
  dateTimeFormat: 'dd/MM/yyyy HH:mm',
  
  /**
   * Time format for time-only displays
   */
  timeFormat: 'HH:mm',
  
  /**
   * Date format for API requests/responses (ISO format)
   */
  apiDateFormat: 'yyyy-MM-dd',
  
  /**
   * DateTime format for API requests/responses
   */
  apiDateTimeFormat: 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'',

  // ============================================================================
  // TABLE CONFIGURATION
  // ============================================================================
  
  table: {
    /**
     * Default page size for tables
     */
    defaultPageSize: 50,
    
    /**
     * Available page size options
     */
    pageSizeOptions: [50, 100, 200, 500],
    
    /**
     * Default table density
     */
    defaultDensity: 'normal', // 'compact', 'normal', 'comfortable'
    
    /**
     * Default date column width
     */
    dateColumnWidth: '120px',
    
    /**
     * Default datetime column width
     */
    dateTimeColumnWidth: '150px',
    
    /**
     * Show row numbers by default
     */
    showRowNumbers: false,
    
    /**
     * Enable row selection by default
     */
    enableRowSelection: true,
    
    /**
     * Default sort direction
     */
    defaultSortDirection: 'asc', // 'asc' or 'desc'
  },

  // ============================================================================
  // UI/UX SETTINGS
  // ============================================================================
  
  ui: {
    /**
     * Table + badge UI theme: 'refined' (new) | 'legacy' (classic).
     * Runtime override: localStorage `tableUiTheme` or window.__applyTableUiTheme('legacy')
     */
    defaultTableUiTheme: 'refined',

    /** Default color theme id (ThemeContext) when user has no saved preference. */
    defaultColorTheme: 'qms_professional',

    /**
     * Show/hide footer component
     */
    showFooter: false,
    
    /**
     * Sidebar configuration
     */
    sidebar: {
      collapsible: true,           // Allow sidebar to be collapsed
      defaultCollapsed: false,     // Start collapsed on desktop
      collapsedWidth: '64px',      // Width when collapsed
      expandedWidth: '288px',      // Width when expanded (w-72 = 288px)
      showLogo: true,              // Show logo in sidebar
      showUserInfo: true,          // Show user info in sidebar
    },
    
    /**
     * Default drawer width for forms
     */
    defaultDrawerWidth: '600px',
    
    /**
     * Search input maximum width
     */
    searchInputMaxWidth: '60%', // Tailwind: max-w-md (448px)
    // Examples of different width values you can use:
    // searchInputMaxWidth: '20rem',    // Smaller (320px)
    // searchInputMaxWidth: '28rem',    // Current (448px) 
    // searchInputMaxWidth: '32rem',    // Medium (512px)
    // searchInputMaxWidth: '40rem',    // Large (640px)
    // searchInputMaxWidth: '100%',     // Full width
    // searchInputMaxWidth: '400px',    // Fixed pixel value
    
    /**
     * Animation duration for transitions (in milliseconds)
     */
    animationDuration: 200,
    
    /**
     * Default notification duration (in milliseconds)
     */
    notificationDuration: 5000,
    
    /**
     * Required field indicator style
     */
    requiredFieldStyle: 'badge', // 'badge', 'pill', 'icon', 'underline', 'minimal'
    
    /**
     * Default modal sizes
     */
    modalSizes: {
      small: '400px',
      medium: '600px',
      large: '800px',
      extraLarge: '1200px'
    },
    
    /**
     * Default form field spacing
     */
    formFieldSpacing: 'normal', // 'compact', 'normal', 'comfortable'
    
    /**
     * Table action buttons visibility
     */
    tableActions: {
      visibility: 'always', // 'always' or 'hover'
      showTooltips: true,  // Show tooltips for action buttons
      buttonSize: 'sm',    // 'xs', 'sm', 'md', 'lg'
      spacing: 'gap-1',    // Spacing between action buttons
      /** px per icon button (used to size the Actions column). */
      buttonWidthPx: { xs: 28, sm: 32, md: 36, lg: 40 },
      actionsColumnPaddingPx: 12,
    },
  },

  // ============================================================================
  // VALIDATION SETTINGS
  // ============================================================================
  
  validation: {
    /**
     * Show validation errors on blur by default
     */
    validateOnBlur: true,
    
    /**
     * Show validation errors on change by default
     */
    validateOnChange: false,
    
    /**
     * Debounce time for validation (in milliseconds)
     */
    validationDebounce: 300,
    
    /**
     * Password minimum length
     */
    passwordMinLength: 8,
    
    /**
     * Maximum file upload size (in bytes)
     */
    maxFileSize: 10 * 1024 * 1024, // 10MB
  },

  // ============================================================================
  // DISPLAY FORMATS
  // ============================================================================
  
  display: {
    /**
     * Number format settings
     */
    number: {
      locale: 'en-US',
      currency: 'USD',
      decimalPlaces: 2,
    },
    
    /**
     * Boolean display values
     */
    boolean: {
      trueLabel: 'Yes',
      falseLabel: 'No',
      trueIcon: '✓',
      falseIcon: '✗',
    },
    
    /**
     * Empty/null value display
     */
    emptyValue: '-',
    
    /**
     * Loading text
     */
    loadingText: 'Loading...',
    
    /**
     * No data text
     */
    noDataText: 'No data available',
  },

  // ============================================================================
  // API SETTINGS
  // ============================================================================
  
  api: {
    /**
     * Request timeout (in milliseconds)
     */
    timeout: 30000,
    
    /**
     * Retry attempts for failed requests
     */
    retryAttempts: 3,
    
    /**
     * Retry delay (in milliseconds)
     */
    retryDelay: 1000,
    
    /**
     * SWR cache duration (in milliseconds)
     */
    cacheDuration: 300000, // 5 minutes
  },

  // ============================================================================
  // FEATURE FLAGS
  // ============================================================================
  
  features: {
    /**
     * Enable advanced search functionality
     */
    enableAdvancedSearch: true,
    
    /**
     * Enable export functionality
     */
    enableExport: true,
    
    /**
     * Enable bulk operations
     */
    enableBulkOperations: true,
    
    /**
     * Enable dark mode
     */
    enableDarkMode: true,
    
    /**
     * Enable real-time updates
     */
    enableRealTimeUpdates: false,
    
    /**
     * Enable audit logging
     */
    enableAuditLogging: true,
  },

  // ============================================================================
  // SYSTEM SETTINGS
  // ============================================================================
  
  system: {
    /**
     * Application name
     */
    appName: 'QMS Application',
    
    /**
     * Application version
     */
    version: '1.0.0',
    
    /**
     * Environment
     */
    environment: process.env.NODE_ENV || 'development',
    
    /**
     * Debug mode
     */
    debugMode: process.env.NODE_ENV === 'development',
    
    /**
     * Session timeout (in milliseconds)
     */
    sessionTimeout: 8 * 60 * 60 * 1000, // 8 hours
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get date format based on context
 * @param {string} context - 'table', 'form', 'display', 'api'
 * @returns {string} Date format string
 */
export const getDateFormat = (context = 'display') => {
  switch (context) {
    case 'table':
    case 'display':
      return appConfig.dateFormat;
    case 'datetime':
      return appConfig.dateTimeFormat;
    case 'api':
      return appConfig.apiDateFormat;
    case 'api-datetime':
      return appConfig.apiDateTimeFormat;
    default:
      return appConfig.dateFormat;
  }
};

/**
 * Get table configuration
 * @param {string} setting - Specific table setting to get
 * @returns {any} Table configuration value
 */
export const getTableConfig = (setting) => {
  return appConfig.table[setting];
};

/**
 * Get UI configuration
 * @param {string} setting - Specific UI setting to get
 * @returns {any} UI configuration value
 */
export const getUIConfig = (setting) => {
  return appConfig.ui[setting];
};

/**
 * Get sidebar configuration
 * @param {string} setting - Specific sidebar setting to get
 * @returns {any} Sidebar configuration value
 */
export const getSidebarConfig = (setting) => {
  return appConfig.ui.sidebar[setting];
};

/**
 * Get table actions configuration
 * @param {string} setting - Specific table actions setting to get
 * @returns {any} Table actions configuration value
 */
/** @deprecated Prefer `getTableActionsConfig` from `tableActionButtons.js` (theme-aware). */
export const getTableActionsConfig = (setting) => {
  return appConfig.ui.tableActions[setting];
};

/** How many action icons a module table may show (row-level canHide delete/edit not counted). */
export function countTableActionButtons(moduleConfig = {}) {
  const ui = moduleConfig.ui || {};
  let count = 0;
  if (ui.showViewButton !== false) count += 1;
  if (ui.showEditButton !== false) count += 1;
  if (ui.showDuplicateButton === true) count += 1;
  if (ui.showDeleteButton !== false) count += 1;
  if (moduleConfig.customHandlers?.onViewHtml) count += 1;
  return count;
}

/** Minimum width so the "ACTIONS" header does not overlap the next column. */
export const TABLE_ACTIONS_COLUMN_MIN_WIDTH_PX = 96;

/** Actions column width from icon count + density. */
export function getTableActionsColumnWidth(moduleConfig = {}) {
  const count = countTableActionButtons(moduleConfig);
  if (count === 0) return undefined;

  const ta = appConfig.ui.tableActions;
  const size = ta.buttonSize || 'sm';
  const perButton = ta.buttonWidthPx?.[size] ?? 32;
  const gap = ta.spacing === 'gap-2' ? 8 : ta.spacing === 'gap-3' ? 12 : 4;
  const pad = ta.actionsColumnPaddingPx ?? 12;
  const iconsWidth = count * perButton + Math.max(0, count - 1) * gap + pad;
  const width = Math.max(iconsWidth, TABLE_ACTIONS_COLUMN_MIN_WIDTH_PX);
  return `${width}px`;
}

export function buildTableActionsColumn(moduleConfig = {}, overrides = {}) {
  const width = getTableActionsColumnWidth(moduleConfig);
  if (!width) return null;
  return {
    key: 'actions',
    header: 'Actions',
    width,
    minWidth: width,
    ...overrides,
  };
}

/**
 * Check if feature is enabled
 * @param {string} feature - Feature name to check
 * @returns {boolean} Whether feature is enabled
 */
export const isFeatureEnabled = (feature) => {
  return appConfig.features[feature] || false;
};

// Export individual sections for convenience
export const dateFormats = {
  display: appConfig.dateFormat,
  dateTime: appConfig.dateTimeFormat,
  time: appConfig.timeFormat,
  api: appConfig.apiDateFormat,
  apiDateTime: appConfig.apiDateTimeFormat,
};

export const tableConfig = appConfig.table;
export const uiConfig = appConfig.ui;
export const tableActionsConfig = appConfig.ui.tableActions;
export const validationConfig = appConfig.validation;
export const displayConfig = appConfig.display;
export const apiConfig = appConfig.api;
export const featureFlags = appConfig.features;
export const systemConfig = appConfig.system;

export default appConfig;
